import { yupResolver } from "@hookform/resolvers";
import { useForm } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import { Button, Form, Icon, Modal } from "semantic-ui-react";
import * as yup from "yup";
import { ControlledInput } from "../../../../../../../../components/ControlledForm";
import { buildReturnUrl } from "../../../../../../../auth/hooks/useAuthIssueAnonymousTokenByOTPInURL";
import { useNotifySubscriberContactMethodsForLoggedInUser } from "../../../../../../hooks/useNotifySubscriberContactMethodsForLoggedInUser";
import {
  isProbablyEmail,
  isProbablyPhoneNumber,
  isValidEmail,
  isValidPhoneNumber,
} from "../ManageEmailOrPhoneNumberCard";
import { FocusScope } from "react-aria";

interface IAddContactMethodModalProps {
  open: boolean;
  onClose: () => void;
  signInRedirectURL?: string;
  onSubmit?: ({
    identifier,
    type,
  }: { identifier: string; type: "Email"; returnUrl: string } | { identifier: string; type: "Phone" }) => void;
  submitTitle: string;
  isLoading?: boolean;
  showSignInLink?: boolean;
}

export const AddSubscriberContactMethodModal = ({
  open,
  onClose,
  onSubmit: onSubmitProp = () => {},
  submitTitle,
  signInRedirectURL,
  isLoading = false,
  showSignInLink = true,
}: IAddContactMethodModalProps) => {
  const params = useParams<{ site: string; id: string }>();

  const { subscriberContactMethods } = useNotifySubscriberContactMethodsForLoggedInUser();

  const onSubmit = async (data: { emailOrPhoneNumber: string }) => {
    if (isValidEmail(data.emailOrPhoneNumber)) {
      onSubmitProp({
        identifier: data.emailOrPhoneNumber,
        type: "Email",
        returnUrl: buildReturnUrl("Email", data.emailOrPhoneNumber),
      });
    }
    if (isValidPhoneNumber(data.emailOrPhoneNumber)) {
      onSubmitProp({
        identifier: data.emailOrPhoneNumber,
        type: "Phone",
      });
    }
  };

  const { control, handleSubmit, errors, watch } = useForm<{
    emailOrPhoneNumber: string;
  }>({
    resolver: yupResolver(
      yup.object().shape({
        emailOrPhoneNumber: yup
          .string()
          .required("Email Address or Phone Number is required")
          .test("emailOrPhoneNumber", "Invalid Email Address or Phone Number", (value: string) => {
            return isValidPhoneNumber(value) || isValidEmail(value);
          }),
      }),
    ),
    defaultValues: {
      emailOrPhoneNumber: "",
    },
  });
  const emailOrPhoneNumber = watch("emailOrPhoneNumber");

  const getInputIconAndType = () => {
    if (!emailOrPhoneNumber)
      return {
        icon: null,
        type: null,
      };
    if (isValidEmail(emailOrPhoneNumber))
      return {
        icon: <Icon name='envelope' color='blue' />,
        type: "Email",
      };
    if (isValidPhoneNumber(emailOrPhoneNumber))
      return {
        icon: <Icon name='phone' color='blue' />,
        type: "Phone",
      };
    if (isProbablyEmail(emailOrPhoneNumber))
      return {
        icon: <Icon name='envelope' />,
        type: "Email",
      };
    if (isProbablyPhoneNumber(emailOrPhoneNumber))
      return {
        icon: <Icon name='phone' />,
        type: "Phone",
      };
    return {
      icon: null,
      type: null,
    };
  };

  const { icon, type } = getInputIconAndType();

  const shouldShowPrimaryPhoneNumberWarning =
    type === "Phone" && !subscriberContactMethods?.some((contactMethod) => contactMethod.type === "Phone");

  return (
    <Modal open={open} onClose={onClose} size='tiny' closeIcon aria-labelledby='add-contact-method-modal-header'>
      <Modal.Header id='add-contact-method-modal-header' as='h2'>
        Add Contact Method
      </Modal.Header>
      <FocusScope contain autoFocus restoreFocus>
        <Modal.Content className='tw-flex tw-flex-col'>
          <Form className='tw-space-y-1' onSubmit={handleSubmit(onSubmit)}>
            <Form.Field className='tw-m-0' error={!!errors.emailOrPhoneNumber}>
              <ControlledInput
                control={control}
                errors={errors}
                name='emailOrPhoneNumber'
                icon={icon}
                iconPosition={"right"}
                fluid
                autoComplete='email'
                label='Email Address or Phone Number'
                inputClassName='tw-border-border-dark focus:tw-ring-2 tw-ring-blue'
              />
            </Form.Field>
          </Form>
          {shouldShowPrimaryPhoneNumberWarning && (
            <span className='tw-mb-[7px] tw-mt-[14px]'>This will be set as your primary contact method</span>
          )}
          {showSignInLink && (
            <span className='tw-my-[7px] '>
              Already have an account?
              <Link
                to={{
                  pathname: `/${params.site}/public/login`,
                  search: signInRedirectURL,
                }}
                className='tw-ml-2 tw-font-bold'
              >
                Sign In
              </Link>
            </span>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button secondary onClick={onClose}>
            Cancel
          </Button>
          <Button
            primary
            disabled={!emailOrPhoneNumber || !!errors.emailOrPhoneNumber}
            loading={isLoading}
            onClick={handleSubmit(onSubmit)}
          >
            {submitTitle}
          </Button>
        </Modal.Actions>
      </FocusScope>
    </Modal>
  );
};
